import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { WhatsAppIcon } from '../components/WhatsAppButton';
import api from '../api';

const BANK_INFO = {
  name: process.env.REACT_APP_BANK_NAME || 'Banka Adı',
  iban: process.env.REACT_APP_BANK_IBAN || 'TR00 0000 0000 0000 0000 0000 00',
  owner: process.env.REACT_APP_BANK_OWNER || 'Hesap Sahibi',
};

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    customer_address: '', notes: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('havale');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [paytrToken, setPaytrToken] = useState('');
  const iframeRef = useRef(null);

  useEffect(() => {
    if (cart.length === 0 && !orderNumber) navigate('/urunler');
  }, [cart, orderNumber, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const createOrder = async (method) => {
    const res = await api.post('/api/orders', {
      ...form,
      total_amount: cartTotal,
      payment_method: method,
      items: cart.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
    });
    return res.data.order_number;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.customer_address) {
      setError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      if (paymentMethod === 'havale') {
        const num = await createOrder('havale');
        setOrderNumber(num);
        clearCart();
      } else {
        const num = await createOrder('kart');
        const tokenRes = await api.post('/api/payment/paytr/token', {
          order_number: num,
          total_amount: cartTotal,
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          customer_address: form.customer_address,
          cart_items: cart.map(i => ({ name: i.name, price: i.price, quantity: i.quantity })),
        });
        setOrderNumber(num);
        setPaytrToken(tokenRes.data.token);
        clearCart();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Sipariş oluşturulurken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // PayTR iframe göster
  if (paytrToken) {
    return (
      <div className="bg-bg-primary min-h-screen pt-40 pb-16">
        <div className="max-w-2xl mx-auto px-4">
          <h1 className="text-2xl font-black text-white mb-6">Kart ile Ödeme</h1>
          <p className="text-text-muted mb-6">Sipariş No: <span className="text-accent-gold font-bold">{orderNumber}</span></p>
          <div className="bg-white rounded-2xl overflow-hidden">
            <iframe
              ref={iframeRef}
              src={`https://www.paytr.com/odeme/guvenli/${paytrToken}`}
              id="paytriframe"
              frameBorder="0"
              scrolling="no"
              style={{ width: '100%', minHeight: '600px', display: 'block' }}
              title="PayTR Ödeme"
            />
          </div>
        </div>
      </div>
    );
  }

  // Havale onay ekranı
  if (orderNumber && paymentMethod === 'havale') {
    return (
      <div className="bg-bg-primary min-h-screen pt-40 pb-16">
        <div className="max-w-xl mx-auto px-4 space-y-6">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <span className="text-3xl">✓</span>
            </div>
            <h1 className="text-3xl font-black text-white">Siparişiniz Alındı!</h1>
            <p className="text-text-muted">Sipariş numaranız:</p>
            <p className="text-2xl font-black text-accent-gold">{orderNumber}</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <h2 className="text-white font-bold text-lg flex items-center gap-2">🏦 Havale / EFT Bilgileri</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">Banka</span>
                <span className="text-white font-semibold">{BANK_INFO.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Hesap Sahibi</span>
                <span className="text-white font-semibold">{BANK_INFO.owner}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-text-muted flex-shrink-0">IBAN</span>
                <span className="text-white font-bold font-mono text-right">{BANK_INFO.iban}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Tutar</span>
                <span className="text-accent-gold font-black text-lg">₺{cartTotal.toLocaleString('tr-TR')}</span>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-yellow-400 text-xs">
                ⚠️ Açıklama kısmına sipariş numaranızı (<strong>{orderNumber}</strong>) yazmayı unutmayın.
              </div>
            </div>
          </div>

          <a
            href={`https://wa.me/905412665154?text=${encodeURIComponent(`Merhaba, ${orderNumber} numaralı siparişim için havale yaptım.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-bold py-4 rounded-xl hover:brightness-110 transition"
          >
            <WhatsAppIcon size={20} />
            Ödemeyi WhatsApp'tan Bildirin
          </a>
        </div>
      </div>
    );
  }

  // Checkout formu
  return (
    <div className="bg-bg-primary min-h-screen pt-40 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-10">Siparişi Tamamla</h1>

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-10">
          {/* Sol: Müşteri bilgileri + ödeme */}
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-5">
              <h2 className="text-white font-bold text-lg">Müşteri Bilgileri</h2>

              {[
                { label: 'Ad Soyad *', name: 'customer_name', type: 'text', required: true },
                { label: 'Telefon *', name: 'customer_phone', type: 'tel', required: true },
                { label: 'E-posta', name: 'customer_email', type: 'email', required: false },
              ].map(f => (
                <div key={f.name}>
                  <label className="block text-sm font-medium text-text-muted mb-2">{f.label}</label>
                  <input
                    type={f.type}
                    name={f.name}
                    value={form[f.name]}
                    onChange={handleChange}
                    required={f.required}
                    className="w-full px-4 py-3 bg-white/5 border border-border-soft rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-accent-gold transition"
                  />
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">Teslimat Adresi *</label>
                <textarea
                  name="customer_address"
                  value={form.customer_address}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-border-soft rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-accent-gold transition resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-muted mb-2">Sipariş Notu</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-4 py-3 bg-white/5 border border-border-soft rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-accent-gold transition resize-none"
                />
              </div>
            </div>

            {/* Ödeme yöntemi */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
              <h2 className="text-white font-bold text-lg">Ödeme Yöntemi</h2>

              {[
                { value: 'havale', label: '🏦 Havale / EFT', desc: 'Banka havalesi ile öde' },
                { value: 'kart', label: '💳 Kredi / Banka Kartı', desc: 'PayTR güvenceli ödeme' },
              ].map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-300 ${
                    paymentMethod === opt.value
                      ? 'border-accent-gold bg-accent-gold/5'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value={opt.value}
                    checked={paymentMethod === opt.value}
                    onChange={() => setPaymentMethod(opt.value)}
                    className="mt-1 accent-yellow-500"
                  />
                  <div>
                    <p className="text-white font-bold">{opt.label}</p>
                    <p className="text-text-muted text-sm">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Sağ: Sipariş özeti */}
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4 sticky top-32">
              <h2 className="text-white font-bold text-lg">Sipariş Özeti</h2>

              <div className="space-y-3 max-h-80 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between items-center gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.name} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{item.name}</p>
                        <p className="text-text-muted text-xs">x{item.quantity}</p>
                      </div>
                    </div>
                    <span className="text-white font-bold flex-shrink-0">
                      ₺{(Number(item.price || 0) * item.quantity).toLocaleString('tr-TR')}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-text-muted">Toplam</span>
                  <span className="text-2xl font-black text-white">₺{cartTotal.toLocaleString('tr-TR')}</span>
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || cart.length === 0}
                className="w-full bg-accent-red hover:bg-red-700 text-white font-black py-4 rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
              >
                {loading ? 'İşleniyor...' : 'Siparişi Onayla →'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
